import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, ListTodo } from 'lucide-react'
import { api } from '@/lib/api'
import { useTaskStore } from '@/store/useTaskStore'
import { TaskCard } from './TaskCard'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

export function TaskList() {
  const { data, isLoading } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.getTasks(),
    refetchInterval: 5000,
  })

  const { tasks, selectedTaskId, selectTask, setTasks, activeRunId, setActiveRunId } = useTaskStore()

  useEffect(() => {
    if (data?.tasks) {
      setTasks(data.tasks)

      // Three-priority activeRunId restoration system
      if (!activeRunId) {
        let taskToRestore = null

        // Priority 1: Running tasks (highest priority)
        taskToRestore = data.tasks.find(t => t.status === 'running' && t.run_id)

        // Priority 2: Currently selected task (if it has a run_id)
        if (!taskToRestore && selectedTaskId) {
          const selectedTask = data.tasks.find(t => t.id === selectedTaskId && t.run_id)
          if (selectedTask) taskToRestore = selectedTask
        }

        // Priority 3: Most recent completed/error task
        if (!taskToRestore) {
          const completedTasks = data.tasks
            .filter(t => (t.status === 'complete' || t.status === 'error') && t.run_id && t.last_run_at)
            .sort((a, b) => new Date(b.last_run_at!).getTime() - new Date(a.last_run_at!).getTime())
          taskToRestore = completedTasks[0]
        }

        if (taskToRestore && taskToRestore.run_id) {
          setActiveRunId(taskToRestore.run_id)
        }
      }
    }
  }, [data, setTasks, activeRunId, setActiveRunId, selectedTaskId])

  // Sync activeRunId when user selects a different task
  useEffect(() => {
    if (selectedTaskId === null) {
      // Clear activeRunId when no task is selected (e.g., New Task clicked)
      setActiveRunId(null)
    } else if (selectedTaskId && tasks.length > 0) {
      const selectedTask = tasks.find(t => t.id === selectedTaskId)
      if (selectedTask && selectedTask.run_id && selectedTask.run_id !== activeRunId) {
        setActiveRunId(selectedTask.run_id)
      }
    }
  }, [selectedTaskId, tasks, activeRunId, setActiveRunId])

  const handleNewTask = () => {
    selectTask(null)
    setActiveRunId(null) // Clear activeRunId when creating new task
  }

  const runningTasks = tasks.filter(t => t.status === 'running')
  const otherTasks = tasks.filter(t => t.status !== 'running')

  return (
    <div className="panel rounded-md p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="section-title">
          Tasks
        </h3>
        <Button size="sm" variant="outline" onClick={handleNewTask}>
          <Plus className="w-4 h-4 mr-1" />
          New
        </Button>
      </div>

      <div className="space-y-3">
        {isLoading ? (
          <>
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </>
        ) : tasks.length === 0 ? (
          <div className="text-center py-8 px-3">
            <div className="inline-flex items-center justify-center w-10 h-10 border border-slate-800 mb-3">
              <ListTodo className="w-5 h-5 text-slate-500" />
            </div>
            <h4 className="text-sm font-medium text-slate-300 mb-2">No tasks yet</h4>
            <p className="text-xs text-slate-500 mb-4 max-w-[200px] mx-auto">
              Create your first task to start discovering high-quality candidates
            </p>
            <Button size="sm" onClick={handleNewTask}>
              <Plus className="w-4 h-4 mr-1" />
              Create Task
            </Button>
          </div>
        ) : (
          <>
            {runningTasks.length > 0 && (
              <div className="space-y-2">
                <h4 className="muted-label flex items-center gap-2 text-slate-300">
                  <span className="w-2 h-2 bg-green-500"></span>
                  Running Tasks ({runningTasks.length})
                </h4>
                {runningTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    isSelected={task.id === selectedTaskId}
                    onClick={() => selectTask(task.id)}
                  />
                ))}
              </div>
            )}

            {otherTasks.length > 0 && (
              <div className="space-y-2">
                {runningTasks.length > 0 && (
                  <h4 className="muted-label">
                    All Tasks ({otherTasks.length})
                  </h4>
                )}
                {otherTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    isSelected={task.id === selectedTaskId}
                    onClick={() => selectTask(task.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
